import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Constants
import { COOKIES_POLICY_BODY, PRIVACY_POLICY_BODY, USE_POLICY_BODY } from 'constants/cookies';

// Services
import { routeToCookiePolicy } from 'services/cookies';
import { isFieldDefined } from 'services/util/auxiliaryUtils';

import type { TCookiesType } from 'types/cookies';

// Components
import CookieBody from './Body';
import CookieHeader from './Header';

interface ICookieConsentProps {
    /** The policy to show over the page. Without it, the policy the cookie policies page was opened with. */
    type?: TCookiesType | null;
    close?: () => void;
    showPolicyHandler?: (type: TCookiesType) => void;
}

// The policy's title, text and date, with the links in its text opening the policies they name.
const policyContent = (type: TCookiesType | null | undefined, openPolicy: (type: TCookiesType) => void) => {
    switch (type) {
        case 'use-conditions':
            return USE_POLICY_BODY(
                () => openPolicy('privacy-policy'),
                () => openPolicy('cookies-policy')
            );
        case 'privacy-policy':
            return PRIVACY_POLICY_BODY(
                () => openPolicy('use-conditions'),
                () => openPolicy('cookies-policy')
            );
        case 'cookies-policy':
            return COOKIES_POLICY_BODY(() => openPolicy('privacy-policy'));
        default:
            return null;
    }
};

const CookieConsent = ({ type, close = () => {}, showPolicyHandler = () => {} }: ICookieConsentProps) => {
    // Shown over the page (signed out) when given a type, or as the cookie policies page.
    const isOverlay = isFieldDefined(type);
    const location = useLocation();
    const navigate = useNavigate();
    const scrollRef = useRef<HTMLDivElement>(null);

    const policyType = isOverlay ? type : (location.state as { type?: TCookiesType } | null)?.type;
    const content = policyContent(policyType, isOverlay ? showPolicyHandler : (linkType) => routeToCookiePolicy(navigate, linkType));

    // Scrolls back to the top when a link opens another policy.
    const shownPolicyType = useRef(policyType);
    useEffect(() => {
        if (shownPolicyType.current !== policyType) {
            shownPolicyType.current = policyType;
            scrollRef.current?.scrollIntoView();
        }
    }, [policyType]);

    return (
        <div className={isOverlay ? 'cookie-consent cookie-consent-not-authenticated' : 'cookie-consent'} ref={scrollRef}>
            {isOverlay && <CookieHeader title={content?.title} close={close} />}
            <CookieBody body={content?.body} updatedDate={content?.updated ?? ''} />
        </div>
    );
};

export default CookieConsent;
